import React from "react";
import { Input } from "../../../common/components/Form/Input";
import { Button } from "../../../common/components/Form/Button";

export interface AddressFormData {
  customerName: string;
  addressLine: string;
  city: string;
  stateProvinceCode: string;
  postalCode: string;
  phone: string;
}

interface LabelAddressFormProps {
  formData: AddressFormData;
  onFormChange: (field: keyof AddressFormData, value: string) => void;
  onSubmit: () => void;
  onCancel: () => void;
  isLoading: boolean;
  error: string | null;
  submitLabel: string;
  loadingLabel: string;
  borderColorClass?: string;
}

/**
 * Reusable address form component for UPS label generation
 * Used in both "Generate Label" and "Regenerate Label" scenarios
 */
export const LabelAddressForm: React.FC<LabelAddressFormProps> = ({
  formData,
  onFormChange,
  onSubmit,
  onCancel,
  isLoading,
  error,
  submitLabel,
  loadingLabel,
  borderColorClass = "border-amber-200",
}) => {
  const { customerName, addressLine, city, stateProvinceCode, postalCode, phone } = formData;
  
  const isFormValid = addressLine && city && stateProvinceCode && postalCode;

  return (
    <div className="space-y-4 mt-4">
      <div className={`bg-white rounded-lg p-4 border ${borderColorClass}`}>
        <h4 className="font-semibold text-gray-800 mb-3">Shipping Address</h4>
        <div className="space-y-3">
          <Input
            label="Full Name"
            name="gen_customerName"
            placeholder="John Doe"
            value={customerName}
            setValue={(val: string) => onFormChange("customerName", val)}
            disabled={isLoading}
          />
          <Input
            label="Street Address"
            name="gen_addressLine"
            placeholder="123 Main Street"
            value={addressLine}
            setValue={(val: string) => onFormChange("addressLine", val)}
            disabled={isLoading}
          />
          <div className="grid grid-cols-2 gap-3">
            <Input
              label="City"
              name="gen_city"
              placeholder="San Diego"
              value={city}
              setValue={(val: string) => onFormChange("city", val)}
              disabled={isLoading}
            />
            <Input
              label="State"
              name="gen_stateProvinceCode"
              placeholder="CA"
              value={stateProvinceCode}
              setValue={(val: string) => onFormChange("stateProvinceCode", val.toUpperCase().slice(0, 2))}
              disabled={isLoading}
              inputMask={(text: string) => text.toUpperCase().slice(0, 2)}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Input
              label="ZIP Code"
              name="gen_postalCode"
              placeholder="92101"
              value={postalCode}
              setValue={(val: string) => onFormChange("postalCode", val)}
              disabled={isLoading}
            />
            <Input
              label="Phone (Optional)"
              name="gen_phone"
              placeholder="+1234567890"
              value={phone}
              setValue={(val: string) => onFormChange("phone", val)}
              disabled={isLoading}
            />
          </div>
        </div>

        {error && (
          <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded text-red-700 text-sm">
            {error}
          </div>
        )}

        <div className="flex gap-3 mt-4">
          <Button
            label={isLoading ? loadingLabel : submitLabel}
            theme="primary"
            size="medium"
            className="flex-1"
            onClick={onSubmit}
            disabled={isLoading || !isFormValid}
          />
          <Button
            label="Cancel"
            theme="secondary"
            size="medium"
            className="flex-1"
            onClick={onCancel}
            disabled={isLoading}
          />
        </div>
      </div>
    </div>
  );
};
